import { Box, Button, Grid, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import ApartmentIcon from '@mui/icons-material/Apartment';
import StairsIcon from '@mui/icons-material/Stairs';
import LayersIcon from '@mui/icons-material/Layers';
import ExploreIcon from '@mui/icons-material/Explore';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import type { ReactNode } from 'react';
import type { RealApartmentDetail } from '@/types';
import { useAnsweredPercentage } from '@/hooks/useAnsweredPercentage';

/** Limite di configurazioni attive per appartamento (port di REACT_APP_CONFIG_LIMIT legacy). */
const CONFIG_LIMIT_PER_APARTMENT = 4;

interface ApartmentCardProps {
  apartment: RealApartmentDetail;
  onOpenConfiguration: (id: number) => void;
  /** Crea una nuova configurazione per questo appartamento (Fase B). */
  onCreateConfiguration?: (realApartmentId: number) => void;
}

const CardRoot = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(2.5, 2.75, 4),
  height: '100%',
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
}));

/** Attributo appartamento (Edificio/Scala/Piano/Quadrante): valore + icona (bordo) e label sotto. */
function ApartmentInfo({ icon, label, value }: { icon: ReactNode; label: string; value: ReactNode }) {
  return (
    <Box sx={{ mt: 1 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          pb: 0.5,
        }}
      >
        <Typography sx={{ fontSize: 18, color: 'text.primary', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {value == null || value === '' ? '—' : value}
        </Typography>
        <Box sx={{ display: 'flex', color: '#C4C4C4', flexShrink: 0 }}>{icon}</Box>
      </Box>
      <Typography sx={{ fontSize: 13, textTransform: 'capitalize', color: 'text.secondary', pt: 0.5 }}>
        {label}
      </Typography>
    </Box>
  );
}

/** Riga configurazione: indicatore + nome + stato ("in sviluppo"/"completata") + percentuale. */
function ApartmentConfigRow({ id, name, onOpen }: { id: number; name: string; onOpen: (id: number) => void }) {
  const percentage = useAnsweredPercentage(id);
  const pct = percentage == null ? null : Math.round(percentage * 100);
  const completed = pct === 100;

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={() => onOpen(id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(id);
        }
      }}
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 1,
        py: 1.25,
        cursor: 'pointer',
        color: completed ? 'secondary.main' : '#5E5E5E',
        borderTop: '1px solid #C4C4C4',
        '&:first-of-type': { borderTop: 0 },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
        {completed ? (
          <CheckCircleIcon color="secondary" sx={{ fontSize: 16 }} aria-hidden="true" />
        ) : (
          <Box sx={{ width: 11, height: 11, borderRadius: '50%', border: '2px solid', borderColor: 'primary.main', flexShrink: 0 }} />
        )}
        <Typography variant="body2" noWrap>
          {name}{' '}
          <Box component="b">{completed ? 'completata' : 'in sviluppo'}</Box>
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap', color: completed ? 'secondary.main' : 'primary.main' }}>
        {pct == null ? '—' : `${pct} %`}
      </Typography>
    </Box>
  );
}

export function ApartmentCard({ apartment, onOpenConfiguration, onCreateConfiguration }: ApartmentCardProps) {
  const { codiceAppartamento, apartment: apt, building, scala, piano, quadrante, configurations } = apartment;
  const hasConfigurations = configurations.length > 0;
  const isFull = configurations.length >= CONFIG_LIMIT_PER_APARTMENT;

  return (
    <CardRoot>
      {/* Header: codice nel riquadro + "Appartamento" + tipologia (maiuscolo), centrato. */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', bgcolor: '#F9F3EE', py: 3, px: 3, mb: 1 }}>
        {codiceAppartamento && (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 56,
              height: 56,
              px: 1,
              mb: 1.5,
              bgcolor: 'background.paper',
              borderRadius: 3,
              fontSize: 30,
              color: 'text.primary',
              lineHeight: 1,
            }}
          >
            {codiceAppartamento}
          </Box>
        )}
        <Typography variant="body2" color="text.secondary">
          Appartamento
        </Typography>
        <Typography sx={{ fontSize: 18, lineHeight: 1.25, textTransform: 'uppercase', fontWeight: 500 }}>
          {apt.value}
        </Typography>
      </Box>

      {/* Attributi: griglia 2×2 (Edificio/Scala · Piano/Quadrante). */}
      <Grid container columnSpacing={2.25}>
        <Grid item xs={6}>
          <ApartmentInfo icon={<ApartmentIcon sx={{ fontSize: 20 }} />} label="Edificio" value={building?.value ?? null} />
        </Grid>
        <Grid item xs={6}>
          <ApartmentInfo icon={<StairsIcon sx={{ fontSize: 20 }} />} label="Scala" value={scala?.value ?? null} />
        </Grid>
        <Grid item xs={6}>
          <ApartmentInfo icon={<LayersIcon sx={{ fontSize: 20 }} />} label="Piano" value={piano} />
        </Grid>
        <Grid item xs={6}>
          <ApartmentInfo icon={<ExploreIcon sx={{ fontSize: 20 }} />} label="Quadrante" value={quadrante} />
        </Grid>
      </Grid>

      {/* Box configurazioni (#F9F3EE) con titolo + righe, oppure stato vuoto. */}
      <Box sx={{ mt: 3, bgcolor: '#F9F3EE', p: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
        {hasConfigurations ? (
          <>
            <Box sx={{ bgcolor: 'background.paper', textAlign: 'center', py: 1.25, mb: 1 }}>
              <Typography variant="caption" sx={{ fontSize: 13 }}>
                LE TUE <Box component="b">CONFIGURAZIONI</Box>
              </Typography>
            </Box>
            <Box>
              {configurations.map((c) => (
                <ApartmentConfigRow key={c.id} id={c.id} name={c.name} onOpen={onOpenConfiguration} />
              ))}
            </Box>
            {onCreateConfiguration != null && !isFull && (
              <Button
                variant="outlined"
                color="primary"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => onCreateConfiguration(apartment.id)}
                sx={{ mt: 2, alignSelf: 'center', bgcolor: 'background.paper' }}
              >
                Nuova configurazione
              </Button>
            )}
            {isFull && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1.5 }}>
                Hai raggiunto il limite di {CONFIG_LIMIT_PER_APARTMENT} configurazioni.
              </Typography>
            )}
          </>
        ) : (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: 2, py: 3 }}>
            <Typography variant="body2" sx={{ fontSize: 13 }}>
              NON HAI ANCORA CONFIGURAZIONI
            </Typography>
            {onCreateConfiguration != null && (
              <Button variant="contained" color="primary" onClick={() => onCreateConfiguration(apartment.id)}>
                Inizia a configurare
              </Button>
            )}
          </Box>
        )}
      </Box>
    </CardRoot>
  );
}
